import React, { useEffect, useState } from 'react';
import {
  AlertOutlined,
  BarChartOutlined,
  ControlOutlined,
  DashboardOutlined,
  DollarOutlined,
  DownOutlined,
  ExperimentOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ProductOutlined,
  SearchOutlined,
  SettingOutlined,
  SlidersOutlined,
  StockOutlined,
  TagsOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Avatar, Button, Tooltip } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { logout } from '../redux/slices/authSlice';
import './AmzAdsLayout.css';

const comingSoonItems = [
  { key: 'dashboard', label: 'Dashboard', icon: <DashboardOutlined /> },
  { key: 'price-rule', label: 'Price Rule Manager', icon: <ControlOutlined />, badge: 'Level 0-4' },
  { key: 'campaigns', label: 'Campaigns', icon: <ExperimentOutlined /> },
  { key: 'niche-product', label: 'Niche & Product', icon: <ProductOutlined /> },
  { key: 'inventory', label: 'Inventory', icon: <TagsOutlined /> },
  { key: 'targeting', label: 'Targeting', icon: <StockOutlined /> },
  { key: 'budgets', label: 'Budgets', icon: <DollarOutlined /> },
  { key: 'rules', label: 'Rules', icon: <SlidersOutlined /> },
];

const bottomItems = [
  { key: 'search-terms', label: 'Search terms', icon: <SearchOutlined /> },
  { key: 'alerts', label: 'Alerts', icon: <AlertOutlined /> },
  { key: 'settings', label: 'Settings', icon: <SettingOutlined /> },
];

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'amzAds.sidebarCollapsed';

const DisabledMenuItem = ({ item, nested = false }) => (
  <Tooltip title="Coming soon" placement="right">
    <div className={`amz-ads-menu-item is-disabled${nested ? ' is-nested' : ''}`}>
      <span className="amz-ads-menu-icon">{item.icon}</span>
      <span className="amz-ads-menu-label">{item.label}</span>
      {item.badge && <span className="amz-ads-level-badge">{item.badge}</span>}
      <span className="amz-ads-coming-soon">Coming soon</span>
    </div>
  </Tooltip>
);

const AmzAdsLayout = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector(state => state.auth);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => (
    window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === 'true'
  ));

  useEffect(() => {
    window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const handleLogout = () => {
    dispatch(logout()).then(() => navigate('/login'));
  };

  const displayName = user?.username || user?.email?.split('@')[0] || 'User';
  const roleName = user?.userRole?.name || user?.role || 'User';
  const isProfitReport = location.pathname === '/amz-ads/profit-report';

  return (
    <div className={`amz-ads-app${sidebarCollapsed ? ' is-sidebar-collapsed' : ''}`}>
      <aside className={`amz-ads-sidebar${sidebarCollapsed ? ' is-collapsed' : ''}`}>
        <div className="amz-ads-sidebar-header">
          <Link to="/home" className="amz-ads-brand" aria-label="Back to home">
            <span className="amz-ads-brand-mark"><BarChartOutlined /></span>
            <span>AMZ ADS</span>
          </Link>
          <Tooltip title={sidebarCollapsed ? 'Mở sidebar' : 'Thu gọn sidebar'} placement="right">
            <Button
              type="text"
              className="amz-ads-sidebar-toggle"
              icon={sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setSidebarCollapsed(current => !current)}
              aria-label={sidebarCollapsed ? 'Mở sidebar' : 'Thu gọn sidebar'}
              aria-expanded={!sidebarCollapsed}
            />
          </Tooltip>
        </div>

        <nav className="amz-ads-nav" aria-label="AMZ ADS navigation">
          <div className="amz-ads-menu-list">
            {comingSoonItems.map(item => <DisabledMenuItem key={item.key} item={item} />)}

            <div className="amz-ads-menu-section">
              <div className="amz-ads-menu-section-title">
                <span className="amz-ads-menu-icon"><BarChartOutlined /></span>
                <span>Report</span>
                <DownOutlined className="amz-ads-section-chevron" />
              </div>
              <div className="amz-ads-report-tree">
                <DisabledMenuItem
                  nested
                  item={{ key: 'performance-report', label: 'Performance Report', icon: <StockOutlined /> }}
                />
                <Link
                  to="/amz-ads/profit-report"
                  className={`amz-ads-menu-item is-nested${isProfitReport ? ' is-active' : ''}`}
                  title={sidebarCollapsed ? 'Profit Report' : undefined}
                >
                  <span className="amz-ads-menu-icon"><DollarOutlined /></span>
                  <span className="amz-ads-menu-label">Profit Report</span>
                </Link>
              </div>
            </div>

            {bottomItems.map(item => <DisabledMenuItem key={item.key} item={item} />)}
          </div>
        </nav>

        <div className="amz-ads-user-panel">
          <Avatar size={34} icon={<UserOutlined />} />
          <div className="amz-ads-user-meta">
            <span className="amz-ads-user-name">{displayName}</span>
            <span className="amz-ads-user-role">{roleName}</span>
          </div>
          <Tooltip title="Log out">
            <Button
              type="text"
              className="amz-ads-logout"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              aria-label="Log out"
            />
          </Tooltip>
        </div>
      </aside>

      <section className="amz-ads-main">
        <main className="amz-ads-content">{children}</main>
      </section>
    </div>
  );
};

export default AmzAdsLayout;
